window.GoTransferLegRoutes = {
    fromRoute(pickup, waypoints, drop) {
        const legs = [];
        let from = { ...pickup };
        waypoints.forEach(waypoint => {
            const { next_departure, ...to } = waypoint;
            legs.push({ from, to });
            from = next_departure ? { ...next_departure } : { ...to, stop_hours: 0 };
        });
        legs.push({ from, to: { ...drop } });
        return legs;
    },
    toRoute(legs) {
        if (!legs.length) return { pickup: { location: '' }, drop: { location: '' }, waypoints: [] };
        return {
            pickup: { ...legs[0].from },
            drop: { ...legs[legs.length - 1].to },
            waypoints: legs.slice(0, -1).map((leg, index) => {
                const to = { ...leg.to };
                const next = legs[index + 1].from;
                if (to.location.trim().toLocaleLowerCase() !== next.location.trim().toLocaleLowerCase()) to.next_departure = { ...next, stop_hours: 0 };
                return to;
            }),
        };
    },
    async resolveIndependent(origin, destination, waypoints, request) {
        const point = value => ({
            lat: typeof value.lat === 'function' ? value.lat() : value.lat,
            lng: typeof value.lng === 'function' ? value.lng() : value.lng,
        });
        const location = value => value.latitude != null && value.longitude != null
            ? { lat: Number(value.latitude), lng: Number(value.longitude) } : value.location;
        const legs = this.fromRoute({ ...point(origin) }, waypoints, { ...point(destination) });
        const routes = [];
        for (const pair of legs) {
            const response = await new Promise((resolve, reject) => request(
                pair.from.lat != null ? point(pair.from) : location(pair.from),
                pair.to.lat != null ? point(pair.to) : location(pair.to),
                (result, status) => status === 'OK' && result ? resolve(result) : reject(status), pair,
            ));
            const score = route => route.legs.reduce((total, leg) => total + Number(leg.duration.value), 0);
            const route = response.routes.reduce((fastest, candidate) => score(candidate) < score(fastest) ? candidate : fastest);
            routes.push(route);
        }
        const paths = routes.map(route => route.overview_path.map(point));
        const metrics = routes.flatMap(route => route.legs.map(leg => ({
            ...leg, start_location: point(leg.start_location), end_location: point(leg.end_location),
        })));
        return {
            map: { path: paths[0], paths, start_location: metrics[0].start_location, end_location: metrics[metrics.length - 1].end_location },
            legs: metrics,
            route_waypoints: waypoints,
        };
    },
};

window.bindHomeLegRoutes = (root, adapter) => {
    const desktop = window.matchMedia('(min-width: 1200px)');
    let legs = null;
    let selected = null;
    let suspended = false;
    let committing = false;
    const editor = window.createHomeRouteEditor(root, {
        getLegs: () => desktop.matches && legs ? legs : [],
        labels: adapter.labels,
        editLeg: (index, endpoint) => { sync(); select(index); adapter.focus(endpoint); },
        removeLeg: index => {
            sync();
            legs.splice(index, 1);
            if (!legs.length) legs = null;
            select(null);
            save();
        },
        moveLeg: (from, to) => {
            sync();
            legs.splice(to, 0, ...legs.splice(from, 1));
            select(null);
            save();
        },
    });
    function select(index) {
        selected = index;
        suspended = true;
        const pair = legs && index === null
            ? { from: { ...legs[0].from }, to: { ...legs[legs.length - 1].to } }
            : legs?.[index] || { from: { location: '' }, to: { location: '' } };
        adapter.writePair(pair);
        suspended = false;
    }
    function sync() {
        if (suspended || committing || !desktop.matches || !legs) return;
        const pair = adapter.readPair();
        if (selected === null) {
            // The main fields summarize the route; row clicks edit a specific leg.
            legs[0].from = pair.from;
            legs[legs.length - 1].to = pair.to;
        } else {
            legs[selected] = pair;
        }
    }
    function route() {
        sync();
        return legs ? window.GoTransferLegRoutes.toRoute(legs) : null;
    }
    function save() {
        const value = route();
        adapter.setWaypoints(value?.waypoints || []);
        adapter.invalidate();
        adapter.persist();
        editor.render();
    }
    function hydrate() {
        const waypoints = adapter.getWaypoints();
        const pair = adapter.readPair();
        legs = waypoints.length ? window.GoTransferLegRoutes.fromRoute(pair.from, waypoints, pair.to) : null;
        selected = null;
        if (desktop.matches && legs) select(null);
        editor.render();
    }
    function add() {
        sync();
        if (!legs) {
            const pair = adapter.readPair();
            if (!pair.from.location || !pair.to.location) { adapter.focus(!pair.from.location ? 'from' : 'to'); return; }
            legs = [pair];
        }
        const incomplete = legs.findIndex(leg => !leg.from.location || !leg.to.location);
        if (incomplete >= 0) {
            select(incomplete);
            adapter.focus(!legs[incomplete].from.location ? 'from' : 'to');
            return;
        }
        if (legs.length >= 5) return;
        legs.push({ from: { ...legs[legs.length - 1].to, stop_hours: 0 }, to: { location: '' } });
        select(legs.length - 1);
        save();
        adapter.focus('to');
    }
    function commit() {
        sync();
        if (!legs) return true;
        const incomplete = legs.findIndex(leg => !leg.from.location || !leg.to.location);
        if (incomplete >= 0) {
            select(incomplete);
            adapter.focus(!legs[incomplete].from.location ? 'from' : 'to');
            return false;
        }
        const value = route();
        committing = true;
        adapter.writePair({ from: value.pickup, to: value.drop });
        adapter.setWaypoints(value.waypoints);
        return true;
    }
    function release() {
        if (!committing) return;
        committing = false;
        select(selected);
        editor.render();
    }
    function reset() {
        legs = null;
        selected = null;
        committing = false;
        editor.render();
    }
    desktop.addEventListener('change', () => {
        if (!desktop.matches && legs) {
            const value = window.GoTransferLegRoutes.toRoute(legs);
            suspended = true;
            adapter.writePair({ from: value.pickup, to: value.drop });
            adapter.setWaypoints(value.waypoints);
            suspended = false;
            legs = null;
        } else if (desktop.matches) hydrate();
        editor.render();
    });
    hydrate();
    return { add, sync, route, commit, release, reset, hydrate, render: editor.render, active: () => desktop.matches && !!legs,
        settled: () => { if (!desktop.matches || !legs) return; sync(); if (legs && legs.every(leg => leg.from.location && leg.to.location)) select(null); save(); } };
};

// Desktop route summaries share the booking form's route state through this adapter.
window.createHomeRouteEditor = (root, { getLegs, removeLeg, moveLeg, editLeg, labels }) => {
    root.closest('form').dataset.homeRouteEditorReady = 'true';
    const list = root.querySelector('[data-route-editor-list]');
    const confirmation = root.querySelector('[data-route-editor-confirmation]');
    const cancel = root.querySelector('[data-route-editor-cancel]');
    const confirm = root.querySelector('[data-route-editor-confirm-remove]');
    const status = root.querySelector('[data-route-editor-status]');
    const desktop = window.matchMedia('(min-width: 1200px)');
    let pendingIndex = null;
    let draggedIndex = null;
    let pendingButton = null;
    let statusTimer;
    let renderedSignature = null;

    const text = (template, number) => template.replace(':number', String(number));
    const closeConfirmation = (restoreFocus = false) => {
        confirmation.hidden = true;
        root.querySelector('.is-removing')?.classList.remove('is-removing');
        if (restoreFocus && pendingButton?.isConnected) pendingButton.focus();
        pendingIndex = null;
        pendingButton = null;
    };
    const positionConfirmation = () => {
        if (!pendingButton || confirmation.hidden) return;
        const bounds = root.getBoundingClientRect();
        const row = pendingButton.closest('li').getBoundingClientRect();
        confirmation.style.top = `${row.top - bounds.top + row.height / 2 - confirmation.offsetHeight / 2}px`;
    };
    const showConfirmation = (index, button) => {
        closeConfirmation();
        const leg = getLegs()[index];
        if (!leg) return;
        pendingIndex = index;
        pendingButton = button;
        button.closest('li').classList.add('is-removing');
        root.querySelector('[data-route-editor-remove-title]').textContent = text(labels.confirmRemove, index + 1);
        root.querySelector('[data-route-editor-remove-route]').textContent = `${leg.from.location} → ${leg.to.location}`;
        confirmation.hidden = false;
        positionConfirmation();
        cancel.focus({ preventScroll: true });
    };
    const makeLocation = (location, index, endpoint) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'home-route-editor__location';
        button.innerHTML = '<i class="fa-solid fa-location-dot" aria-hidden="true"></i><span></span>';
        button.querySelector('span').textContent = location || labels.missingLocation;
        button.title = location || labels.missingLocation;
        button.setAttribute('aria-label', `${text(labels.editLeg, index + 1)}: ${location || labels.missingLocation}`);
        button.addEventListener('click', () => editLeg(index, endpoint));
        return button;
    };
    const render = () => {
        const legs = getLegs();
        const signature = JSON.stringify([desktop.matches, legs]);
        if (signature === renderedSignature) return;
        renderedSignature = signature;
        closeConfirmation();
        root.hidden = !desktop.matches || !legs.length;
        list.replaceChildren();
        legs.forEach((leg, index) => {
            const row = document.createElement('li');
            row.className = 'home-route-editor__row';
            row.dataset.routeEditorLeg = index;
            row.innerHTML = '<button type="button" class="home-route-editor__handle" draggable="true"><i class="fa-solid fa-grip-vertical" aria-hidden="true"></i></button><span class="home-route-editor__number"></span><span class="home-route-editor__label"></span><i class="fa-solid fa-arrow-right home-route-editor__arrow" aria-hidden="true"></i><button type="button" class="home-route-editor__remove"><i class="fa-regular fa-trash-can" aria-hidden="true"></i></button>';
            row.querySelector('.home-route-editor__number').textContent = index + 1;
            row.querySelector('.home-route-editor__label').textContent = text(labels.leg, index + 1);
            const handle = row.querySelector('.home-route-editor__handle');
            handle.setAttribute('aria-label', text(labels.reorderLeg, index + 1));
            handle.title = text(labels.reorderLeg, index + 1);
            handle.addEventListener('dragstart', event => {
                closeConfirmation();
                draggedIndex = index;
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', String(index));
                row.classList.add('is-dragging');
            });
            handle.addEventListener('dragend', () => {
                draggedIndex = null;
                root.querySelectorAll('.is-dragging, .is-drop-target').forEach(item => item.classList.remove('is-dragging', 'is-drop-target'));
            });
            handle.addEventListener('keydown', event => {
                if (!['ArrowUp', 'ArrowDown'].includes(event.key)) return;
                event.preventDefault();
                const nextIndex = index + (event.key === 'ArrowUp' ? -1 : 1);
                if (nextIndex < 0 || nextIndex >= legs.length) return;
                moveLeg(index, nextIndex);
                render();
                list.children[nextIndex]?.querySelector('.home-route-editor__handle').focus();
            });
            row.addEventListener('dragover', event => {
                if (draggedIndex === null || draggedIndex === index) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';
                row.classList.add('is-drop-target');
            });
            row.addEventListener('dragleave', event => {
                if (!row.contains(event.relatedTarget)) row.classList.remove('is-drop-target');
            });
            row.addEventListener('drop', event => {
                event.preventDefault();
                if (draggedIndex === null || draggedIndex === index) return;
                moveLeg(draggedIndex, index);
                draggedIndex = null;
                render();
                list.children[index]?.querySelector('.home-route-editor__handle').focus();
            });
            const remove = row.querySelector('.home-route-editor__remove');
            remove.setAttribute('aria-label', text(labels.removeLeg, index + 1));
            remove.addEventListener('click', () => showConfirmation(index, remove));
            row.insertBefore(makeLocation(leg.from.location, index, 'from'), row.querySelector('.home-route-editor__arrow'));
            row.insertBefore(makeLocation(leg.to.location, index, 'to'), remove);
            list.append(row);
        });
        root.closest('.home-page')?.classList.toggle('home-page--multi-leg', desktop.matches && legs.length > 0);
    };
    cancel.addEventListener('click', () => closeConfirmation(true));
    confirm.addEventListener('click', () => {
        if (pendingIndex === null) return;
        const index = pendingIndex;
        closeConfirmation();
        removeLeg(index);
        render();
        status.hidden = false;
        clearTimeout(statusTimer);
        statusTimer = setTimeout(() => { status.hidden = true; }, 4000);
        list.children[Math.min(index, list.children.length - 1)]?.querySelector('.home-route-editor__remove').focus();
    });
    confirmation.addEventListener('keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); closeConfirmation(true); }
        if (event.key === 'Tab') {
            event.preventDefault();
            (document.activeElement === cancel ? confirm : cancel).focus();
        }
    });
    document.addEventListener('pointerdown', event => {
        if (!confirmation.hidden && !confirmation.contains(event.target) && !pendingButton?.contains(event.target)) closeConfirmation();
    });
    new ResizeObserver(positionConfirmation).observe(root);
    desktop.addEventListener('change', render);
    render();
    return { render, closeConfirmation };
};
